import { Fragment, useMemo } from 'react'

import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@tribeplatform/react-ui-kit/Sheet'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { usePopoverTour } from '../Onboarding/AdminModeOnboarding/PopoverTourContext.js'
import { PopoverTourStep } from '../Onboarding/AdminModeOnboarding/PopoverTourStep.js'
import { useAdminModeOnboarding } from '../Onboarding/AdminModeOnboarding/useAdminModeOnboarding.js'

import { routeModeFactory } from './modes/factory.js'
import { useMode } from './useMode.js'

export function ModeSwitcher() {
  const { $t } = useI18n()
  const modes = useStaticIntl(routeModeFactory)
  const mode = useMode()
  const { startTour } = usePopoverTour()
  const { hasCompletedTour } = useAdminModeOnboarding()

  const restModes = useMemo(() => {
    return Object.fromEntries(
      Object.entries(modes).filter(([key]) => key !== mode),
    )
  }, [modes, mode])

  return (
    <PopoverTourStep
      step={0}
      wrapperClassName="h-full"
      title={
        <T
          id="AdminModeOnboarding.PopoverTour.ModeSwitcher.Title"
          defaultMessage="Manage your {COMMUNITY}"
        />
      }
      description={
        <T
          id="AdminModeOnboarding.PopoverTour.ModeSwitcher.Description"
          defaultMessage="Click on the mode switcher to explore different modes."
        />
      }
    >
      <div className="h-full flex items-center">
        <div className="h-full hidden lg:block">
          <Dropdown placement="bottom-end" className="h-full">
            <Dropdown.Button className="border-none bg-none! rounded-none h-full">
              <div className="flex items-center gap-2">
                <SvgIcon name={modes[mode].icon} />
                <span>{modes[mode].label}</span>
              </div>
            </Dropdown.Button>
            <Dropdown.Items>
              {hasCompletedTour && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="info" size="md" />}
                  onClick={() => startTour()}
                >
                  <div className="flex flex-col items-start">
                    <Text className="text-sm">
                      <T
                        id="AdminModeOnboarding.Trigger.Title"
                        defaultMessage="We've made some updates"
                      />
                    </Text>
                    <Text className="!text-action-primary text-sm">
                      <T
                        id="AdminModeOnboarding.Trigger.Description"
                        defaultMessage="See what's new →"
                      />
                    </Text>
                  </div>
                </Dropdown.Item>
              )}
              {Object.entries(restModes).map(([key, value]) => (
                <Fragment key={key}>{value.element}</Fragment>
              ))}
            </Dropdown.Items>
          </Dropdown>
        </div>
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger className="mx-2" asChild>
              <IconButton
                variant="plainNeutral"
                icon={<SvgIcon name={modes[mode].icon} />}
                accessibilityLabel={$t({
                  defaultMessage: 'Switch mode',
                  id: 'Generics.SwitchMode',
                })}
              />
            </SheetTrigger>
            <SheetContent side="top">
              <div className="p-4 space-y-4">
                <SheetTitle>
                  <Text variant="heading2xs">
                    <T id="Generics.SwitchMode" defaultMessage="Switch mode" />
                  </Text>
                </SheetTitle>
                <ul className="flex flex-col gap-1">
                  {Object.entries(modes).map(([key, value]) => (
                    <li key={key}>{value.sheetElement}</li>
                  ))}
                  {hasCompletedTour && (
                    <li>
                      <SheetClose
                        className="flex items-center gap-2 p-3 w-full"
                        onClick={() => startTour()}
                      >
                        <SvgIcon name="info" />
                        <div className="flex flex-col items-start">
                          <Text className="text-sm">
                            <T
                              id="AdminModeOnboarding.Trigger.Title"
                              defaultMessage="We've made some updates"
                            />
                          </Text>
                          <Text className="!text-action-primary text-sm">
                            <T
                              id="AdminModeOnboarding.Trigger.Description"
                              defaultMessage="See what's new →"
                            />
                          </Text>
                        </div>
                      </SheetClose>
                    </li>
                  )}
                </ul>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </PopoverTourStep>
  )
}
